import { Fragment } from "react";
import { Popover, Transition, Menu } from "@headlessui/react";
import { MenuIcon, XIcon, ChevronDownIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";

const LANGUAGES = [
  { code: "es", label: "Español",    flag: "🇬🇹" },
  { code: "en", label: "English",    flag: "🇺🇸" },
  { code: "pt", label: "Português",  flag: "🇧🇷" },
  { code: "zh", label: "中文",        flag: "🇨🇳" },
];

function LangToggle({ mobile = false }) {
  const { i18n } = useTranslation();
  const current = LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) ?? LANGUAGES[0];

  if (mobile) {
    return (
      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium text-gray-200 hover:bg-white/10 hover:text-cyan-400 transition-colors w-full">
          <span className="text-lg">{current.flag}</span>
          {current.label}
          <ChevronDownIcon className="h-4 w-4 ml-auto" aria-hidden="true" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute left-0 mt-1 w-40 rounded-lg bg-brand-dark border border-white/10 shadow-lg focus:outline-none z-50">
            {LANGUAGES.map((lang) => (
              <Menu.Item key={lang.code}>
                {({ active }) => (
                  <button
                    onClick={() => i18n.changeLanguage(lang.code)}
                    className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg transition-colors
                      ${active ? "bg-white/10 text-cyan-400" : "text-gray-300"}
                      ${current.code === lang.code ? "font-semibold text-cyan-400" : ""}`}
                  >
                    <span>{lang.flag}</span>
                    {lang.label}
                    {current.code === lang.code && (
                      <span className="ml-auto h-1.5 w-1.5 rounded-full bg-cyan-400" />
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    );
  }

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-xs font-semibold text-gray-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-all">
        <span>{current.flag}</span>
        {current.code.toUpperCase()}
        <ChevronDownIcon className="h-3 w-3" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-36 rounded-lg bg-brand-dark border border-white/10 shadow-xl focus:outline-none z-50">
          {LANGUAGES.map((lang) => (
            <Menu.Item key={lang.code}>
              {({ active }) => (
                <button
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg transition-colors
                    ${active ? "bg-white/10 text-cyan-400" : "text-gray-300"}
                    ${current.code === lang.code ? "font-semibold text-cyan-400" : ""}`}
                >
                  <span>{lang.flag}</span>
                  {lang.label}
                  {current.code === lang.code && (
                    <span className="ml-auto h-1.5 w-1.5 rounded-full bg-cyan-400" />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default function NavBar() {
  const { t } = useTranslation();

  const navigation = [
    { name: t("NavBar.Home"), href: "#inicio" },
    { name: t("NavBar.About"), href: "#nosotros" },
    { name: t("NavBar.Mission"), href: "#mision" },
    { name: t("NavBar.Vision"), href: "#vision" },
    { name: t("NavBar.Team"), href: "#equipo" },
    { name: t("NavBar.Contact"), href: "#contacto" },
  ];

  return (
    <Popover as="header" className="fixed z-20 w-full">
      <div className="bg-brand-dark/95 backdrop-blur-sm border-b border-white/10">
        <nav
          className="relative max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4"
          aria-label="Global"
        >
          <div className="flex items-center flex-1">
            <div className="flex items-center justify-between w-full md:w-auto">
              <a href="#inicio" className="flex items-center">
                <img
                  className="h-9 w-auto"
                  src="/img/icon.png"
                  alt="SIS-GT Tecnología"
                />
              </a>
              <div className="-mr-2 flex items-center gap-2 md:hidden">
                <LangToggle />
                <Popover.Button className="bg-white/10 rounded-md p-2 inline-flex items-center justify-center text-gray-300 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-400">
                  <span className="sr-only">Abrir menú</span>
                  <MenuIcon className="h-6 w-6" aria-hidden="true" />
                </Popover.Button>
              </div>
            </div>

            <div className="hidden space-x-8 md:flex md:ml-10">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-sm font-medium text-gray-300 hover:text-cyan-400 transition-colors duration-200"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>

          <div className="hidden md:flex md:items-center gap-3">
            <LangToggle />
            <a
              href="#contacto"
              className="inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-medium text-white bg-brand-gradient hover:opacity-90 transition-opacity shadow-lg shadow-violet-500/25"
            >
              {t("NavBar.ContactBtn")}
            </a>
          </div>
        </nav>
      </div>

      <Transition
        as={Fragment}
        enter="duration-150 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Popover.Panel
          focus
          className="absolute top-0 inset-x-0 p-2 transition transform origin-top md:hidden z-50"
        >
          <div className="rounded-xl shadow-2xl bg-brand-dark border border-white/10 overflow-hidden">
            <div className="px-5 pt-4 flex items-center justify-between">
              <img
                className="h-8 w-auto"
                src="/img/logo.png"
                alt="SIS-GT Tecnología"
              />
              <Popover.Button className="bg-white/10 rounded-md p-2 inline-flex items-center justify-center text-gray-300 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-400">
                <span className="sr-only">Cerrar menú</span>
                <XIcon className="h-6 w-6" aria-hidden="true" />
              </Popover.Button>
            </div>
            <div className="pt-5 pb-6">
              <div className="px-2 space-y-1">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-200 hover:bg-white/10 hover:text-cyan-400 transition-colors"
                  >
                    {item.name}
                  </a>
                ))}
                <LangToggle mobile />
              </div>
              <div className="mt-6 px-5">
                <a
                  href="#contacto"
                  className="block w-full text-center px-4 py-2 rounded-full text-sm font-medium text-white bg-brand-gradient hover:opacity-90 transition-opacity"
                >
                  {t("NavBar.ContactBtn")}
                </a>
              </div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}
